import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/lib/api/client';

// ── All uploaded documents, foldered (#54) ──────────────────────────────────
// Every file in the system grouped into expandable folders — one per job
// (client · job title), plus General for unattached uploads.

interface Doc {
  id: number; file_name: string; label?: string | null; caption?: string | null;
  file_type?: string | null; file_size?: number | null; file_path: string;
  is_photo?: number; document_category?: string | null; uploaded_at: string;
  job_id?: number | null; job_title?: string | null; job_number?: string | null;
  client_id?: number | null; client_name?: string | null;
}

const fmtSize = (b?: number | null) => {
  if (!b) return '';
  if (b > 1048576) return `${(b / 1048576).toFixed(1)} MB`;
  if (b > 1024) return `${Math.round(b / 1024)} KB`;
  return `${b} B`;
};

const iconFor = (d: Doc) => {
  if (d.is_photo) return 'image';
  const t = (d.file_type || d.file_name || '').toLowerCase();
  if (t.includes('pdf')) return 'picture_as_pdf';
  if (/(png|jpe?g|gif|webp|heic)/.test(t)) return 'image';
  if (/(xls|csv|sheet)/.test(t)) return 'table';
  if (/(doc|word)/.test(t)) return 'description';
  return 'draft';
};

export function AllDocsFolders() {
  const navigate = useNavigate();
  const [docs, setDocs] = useState<Doc[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<Set<string>>(new Set());

  useEffect(() => {
    apiClient.get('/documents/all')
      .then(r => setDocs(r.data.data ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const folders = new Map<string, { title: string; sub: string; jobPath?: string; docs: Doc[] }>();
  for (const d of docs) {
    const key = d.job_id ? `job-${d.job_id}` : 'general';
    if (!folders.has(key)) {
      folders.set(key, d.job_id
        ? {
            title: d.job_title || `Job ${d.job_number || d.job_id}`,
            sub: [d.client_name, d.job_number].filter(Boolean).join(' · '),
            jobPath: d.client_id || d.client_name ? `/clients/${d.client_id}/jobs/${d.job_id}/documents` : `/jobs/${d.job_id}`,
            docs: [],
          }
        : { title: 'General', sub: 'not attached to a job', docs: [] });
    }
    folders.get(key)!.docs.push(d);
  }

  const toggle = (k: string) => setOpen(o => {
    const n = new Set(o);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });

  const openDoc = (d: Doc) => {
    const token = localStorage.getItem('accessToken');
    fetch(`/api/documents/${d.id}/render`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { if (!res.ok) throw new Error(); return res.blob(); })
      .then(blob => window.open(URL.createObjectURL(blob), '_blank'))
      .catch(() => {
        const p = d.file_path.startsWith('/') ? d.file_path : `/${d.file_path}`;
        window.open(p, '_blank');
      });
  };

  if (loading) return <div className="h-16 animate-pulse rounded-xl bg-gray-100" />;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
      <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-2">
        All files — {docs.length} document{docs.length === 1 ? '' : 's'} in {folders.size} folder{folders.size === 1 ? '' : 's'}
      </p>
      {docs.length === 0 ? (
        <p className="text-sm text-gray-400">Nothing uploaded yet — files added on jobs appear here automatically.</p>
      ) : [...folders.entries()].map(([key, f]) => (
        <div key={key} className="border-t border-gray-50 first:border-0">
          <button onClick={() => toggle(key)} className="w-full flex items-center gap-2 py-2 text-left">
            <span className="material-symbols-outlined text-[#C77D3F] text-xl">
              {open.has(key) ? 'folder_open' : 'folder'}
            </span>
            <span className="flex-1 min-w-0">
              <span className="text-sm font-semibold text-gray-900">{f.title}</span>
              {f.sub && <span className="text-xs text-gray-400 ml-2">{f.sub}</span>}
            </span>
            <span className="text-xs text-gray-400">{f.docs.length}</span>
            <span className="material-symbols-outlined text-gray-300 text-lg">
              {open.has(key) ? 'expand_less' : 'expand_more'}
            </span>
          </button>
          {open.has(key) && (
            <div className="pb-2 pl-7 space-y-0.5">
              {f.jobPath && (
                <button onClick={() => navigate(f.jobPath!)}
                  className="text-[11px] font-semibold text-[#8a5526] hover:underline mb-1">
                  Open job documents →
                </button>
              )}
              {f.docs.map(d => (
                <button key={d.id} onClick={() => openDoc(d)}
                  className="w-full flex items-center gap-2 py-1 text-left hover:bg-gray-50 rounded-lg px-1.5">
                  <span className="material-symbols-outlined text-gray-400 text-lg">{iconFor(d)}</span>
                  <span className="flex-1 min-w-0 text-sm text-gray-700 truncate">
                    {d.label || d.caption || d.file_name}
                  </span>
                  {d.document_category && (
                    <span className="text-[10px] bg-gray-100 text-gray-500 rounded-full px-1.5 py-0.5">{d.document_category}</span>
                  )}
                  <span className="text-[11px] text-gray-400 whitespace-nowrap">{fmtSize(d.file_size)}</span>
                  <span className="text-[11px] text-gray-400 whitespace-nowrap">
                    {new Date(d.uploaded_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
